import { pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { AUTO_PRICING_RESULT_TYPES_OPTIONS_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  AutoPricingResultType,
  Feature,
  FilterOperationType,
  ListingQuery,
} from 'src/WebApiController';

const EligibleResultTypes = [
  AutoPricingResultType.InsufficientComparables,
  AutoPricingResultType.PriceAboveCeiling,
  AutoPricingResultType.PriceDecreasesPaused,
  AutoPricingResultType.PriceHitHardFloor,
  AutoPricingResultType.PriceBelowMaxAllowedDrop,
  AutoPricingResultType.PriceHitRelativeFloor,
  AutoPricingResultType.AllPriceUpdatesPaused,
  AutoPricingResultType.PriceSuccess,
];

const OtherResultTypes = Object.keys(AUTO_PRICING_RESULT_TYPES_OPTIONS_TO_CID)
  .map((type: string): AutoPricingResultType => type as AutoPricingResultType)
  .filter((type: AutoPricingResultType) => !EligibleResultTypes.includes(type));

export type AutoPricingFilter = {
  autoPricingResultTypes: ListingQuery['autoPricingResultTypes'];

  operations?: {
    autoPricingResultTypesOp?: FilterOperationType | null;
  } | null;
};

export const useAutoPricingResultFilter = <T extends AutoPricingFilter>(
  tempQuery: T,
  setTempQuery: (_: T) => void,
  supportMultiOperation = false
) => {
  const hasCleanUpOutdatedPricingStateFeature = useUserHasFeature(
    Feature.CleanUpOutdatedPricingState
  );

  const pricingResultTypeToContentId = useMemo(() => {
    if (hasCleanUpOutdatedPricingStateFeature) {
      const eligibleTypeToContentId = pick(
        AUTO_PRICING_RESULT_TYPES_OPTIONS_TO_CID,
        EligibleResultTypes
      );
      Object.assign(eligibleTypeToContentId, {
        [AutoPricingResultType.None]: ContentId.Other,
      });

      return eligibleTypeToContentId as Record<
        AutoPricingResultType,
        ContentId
      >;
    }
    return AUTO_PRICING_RESULT_TYPES_OPTIONS_TO_CID;
  }, [hasCleanUpOutdatedPricingStateFeature]);

  const sanitizeSelectedTypes = useCallback(
    (types: AutoPricingResultType[]): AutoPricingResultType[] => {
      if (hasCleanUpOutdatedPricingStateFeature) {
        // Selecting other (wrapped with None) will include all other types;
        const mappedTypes = types
          // filter out the other types
          .filter(
            (type) =>
              EligibleResultTypes.includes(type) ||
              type === AutoPricingResultType.None
          )
          .flatMap((type) => {
            if (type === AutoPricingResultType.None) {
              return OtherResultTypes;
            }
            return [type];
          });
        return Array.from(new Set(mappedTypes));
      }
      return types;
    },
    [hasCleanUpOutdatedPricingStateFeature]
  );

  const onUpdateResultTypes = useCallback(
    (types: AutoPricingResultType[], operation: FilterOperationType | null) => {
      const updatedTypes = sanitizeSelectedTypes(types);
      // TODO: We should have it such that if they click on the placeholder, it could clear out the options
      // This should not be default though because sometimes the placeholder really is just a placeholder.
      const isChanged = !isEqual(
        updatedTypes,
        tempQuery.autoPricingResultTypes
      );
      setTempQuery({
        ...tempQuery,
        autoPricingResultTypes: isChanged ? updatedTypes : [],
        operations: supportMultiOperation
          ? {
              ...tempQuery.operations,
              autoPricingResultTypesOp: operation ?? null,
            }
          : null,
      });
    },
    [sanitizeSelectedTypes, setTempQuery, tempQuery, supportMultiOperation]
  );

  return {
    pricingResultTypeToContentId,
    onUpdateResultTypes,
  };
};
